import type {
  AccountAnnualReturnsResponse,
  BrokerVerifiedFlatInput,
  AccountType,
  AccountSnapshotInput,
  Broker,
  PortfolioMandate,
  CreateAccountInput,
  LoginInput,
  RegisterInput,
  UpdatePositionStopInput,
  UpdateAccountInput,
  RuleConfigUpdateInput,
  TradePlanInput,
  TradePlanPreviewInput,
  TradePlanStatus,
  CreateJournalEntryInput,
  UpdateJournalEntryInput,
  JournalEntryType,
  WeeklyReviewFlagsSummary,
  WeeklyReviewMetrics,
  WeeklyReviewNotesInput,
  BehaviouralFlagKind,
  Severity,
  NotificationKind,
  NotificationPreferences,
  UpdatePreferencesInput,
  UpdatePositionOverrideInput,
  UpdateTradeInput,
  UserConfirmedFlatInput,
  ReviewMode,
  SubmitTradeReviewInput,
  UpsertHlSecurityMappingInput,
  OhlcPriceBar,
  TradingNextActionsResponse,
  TradingPortfolioHeatResponse,
  CreateInvestmentThesisInput,
  CreateInvestmentThesisSuccessorInput,
  InvestmentAllocationResponse,
  InvestmentPortfolioResponse,
  UpdateInvestmentThemeInput,
  RebindInvestmentThesisIdentityInput,
  UpdateInvestmentThesisInput,
  InvestmentPortfolioRole,
  InvestmentReviewCadence,
  InvestmentThesisIdentitySource,
  InvestmentThesisStatus,
  OAuthAuthorizeParams,
  OAuthAuthorizePreviewDto,
  OAuthGrantDto,
  OAuthRedirectDto,
} from 'shared';

import { apiClient } from './client';

export async function getInvestmentPortfolio(): Promise<InvestmentPortfolioResponse> {
  const { data } = await apiClient.get<InvestmentPortfolioResponse>(
    '/investment-allocation/portfolio',
  );
  return data;
}

export async function updateInvestmentTheme(input: UpdateInvestmentThemeInput): Promise<void> {
  await apiClient.put('/investment-allocation/theme', input);
}

export interface UserDto {
  id: string;
  email: string;
  name: string | null;
  timezone: string;
  baseCurrency: string;
  userPreferences?: NotificationPreferences;
}

export interface AccountDto {
  id: string;
  name: string;
  broker: Broker;
  accountType: AccountType;
  mandate: PortfolioMandate;
  currency: string;
  externalId?: string | null;
  createdAt: string;
  deletedAt: string | null;
  investmentThesisCount?: number;
}

export interface InvestmentThesisDto {
  id: string;
  accountId: string;
  identity: {
    source: InvestmentThesisIdentitySource;
    value: string;
    fingerprint: string | null;
  };
  displaySymbol: string;
  status: InvestmentThesisStatus;
  portfolioRole: InvestmentPortfolioRole | null;
  thesisSummary: string | null;
  targetAllocation: string | null;
  maximumAllocation: string | null;
  timeHorizon: string | null;
  bullCase: string | null;
  baseCase: string | null;
  bearCase: string | null;
  thesisBreakers: string[];
  keyKpis: string[];
  valuationNotes: string | null;
  reviewCadence: InvestmentReviewCadence | null;
  nextReviewAt: string | null;
  closedAt: string | null;
  supersedesThesisId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type InvestmentThesisMatchStatus =
  | 'MATCHED'
  | 'THESIS_MISSING'
  | 'IDENTITY_AMBIGUOUS'
  | 'IDENTITY_REVIEW_REQUIRED';

export interface InvestmentHoldingDto {
  id: string;
  code: string;
  symbol: string;
  stock: string;
  units: string;
  price: string;
  value: string;
  cost: string;
  gainLoss: string;
  gainLossPct: string;
  thesisMatchStatus: InvestmentThesisMatchStatus;
  thesis: InvestmentThesisDto | null;
}

export interface InvestmentHoldingsDto {
  accountId: string;
  asOf: string | null;
  currency: string;
  holdings: InvestmentHoldingDto[];
}

export interface SnapshotDto {
  id: string;
  accountId: string;
  asOfDate: string;
  nav: string;
  cash: string;
  stockMarketValue?: string;
  marginUsed: string;
  buyingPower: string;
  peakNavTrailing90d: string;
  currency?: string;
  sourceCurrency?: string | null;
  fxRate?: string | null;
  fxRateSource?: string | null;
  createdAt: string;
}

export interface CombinedNavSnapshotDto {
  userId: string;
  asOfDate: string;
  nav: string;
  cash: string;
  stockMarketValue: string;
  marginUsed: string;
  buyingPower: string;
  peakNavTrailing90d: string;
  currency: string;
  sourceCurrency: string | null;
  fxRate: string | null;
  fxRateSource: string | null;
  sourceAccountIds: string[];
  createdAt: string;
}

export interface PortfolioMetricsDto {
  nav: string;
  cash: string;
  marginUsed: string;
  dayPnl: string;
  weekPnl: string;
  monthPnl: string;
  ytdPnl: string;
  drawdownPct: number;
  peakNav: string;
  winRate: number | null;
  expectancyR: number | null;
  profitFactor: number | null;
  avgWinUsd: string | null;
  avgLossUsd: string | null;
  avgHoldingDays: number | null;
  themeExposure: Record<string, number>;
  concentrationHhi: number;
  portfolioHeat: number;
}

export interface PortfolioSummaryPositionDto {
  symbol: string;
  rMultiple: string;
  unrealizedPnlPct: string;
}

export interface PortfolioSummaryDto {
  totalMv: string;
  totalUnrealizedPnlDollar: string;
  totalUnrealizedPnlPct: string;
  totalRiskDollar: string;
  portfolioHeatPct: string;
  bestPosition: PortfolioSummaryPositionDto | null;
  worstPosition: PortfolioSummaryPositionDto | null;
  openPositionCount: number;
  navHistory: Array<{
    date: string;
    nav: string;
  }>;
}

export interface DrawdownHistoryPointDto {
  date: string;
  nav: string;
  peakNav: string;
  drawdownPct: number;
}

export interface PnlHistoryPointDto {
  date: string;
  pnl: string;
}

export type AnalyticsPeriod = '7d' | '30d' | '90d' | 'ytd' | 'all';

export interface AnalyticsOverviewDto {
  cumulativePnl: Array<{ date: string; pnl: number }>;
  totalPnl: number;
  winRate: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  avgRMultiple: number;
  tradeCount: number;
  winCount: number;
  lossCount: number;
  bestDay: { date: string; pnl: number };
  worstDay: { date: string; pnl: number };
  longestWinStreak: number;
  longestLossStreak: number;
  avgHoldingDays: number;
}

export interface AnalyticsCalendarDayDto {
  date: string;
  pnl: number;
  tradeCount: number;
}

export interface AnalyticsSymbolDto {
  symbol: string;
  totalPnl: number;
  tradeCount: number;
  winRate: number;
  avgHoldDays: number;
}

export interface AnalyticsSymbolLotDto {
  id: string;
  openedAt: string;
  closedAt: string;
  quantity: string;
  entryPrice: string;
  exitPrice: string;
  realizedPnl: string;
  holdingDays: number;
  win: boolean;
  entryTradeId: string | null;
  exitTradeId: string | null;
}

export interface AnalyticsClosedLotDto extends AnalyticsSymbolLotDto {
  symbol: string;
}

export interface AnalyticsClosedLotsDto {
  from: string;
  to: string;
  totalPnl: number;
  tradeCount: number;
  winRate: number;
  bySymbol: AnalyticsSymbolDto[];
  lots: AnalyticsClosedLotDto[];
}

export interface AnalyticsFlagImpactDto {
  withFlags: { avgPnl: number; tradeCount: number; totalPnl: number };
  withoutFlags: { avgPnl: number; tradeCount: number; totalPnl: number };
  byFlagType: Array<{
    flagType: string;
    avgPnl: number;
    tradeCount: number;
    totalPnl: number;
  }>;
}

export interface ImportRowDto {
  id: string;
  rowIndex: number;
  parsedJson: Record<string, unknown>;
  errorMessage: string | null;
  isDuplicate: boolean;
  createdAt: string;
}

export interface ImportDto {
  id: string;
  accountId: string;
  broker: Broker;
  fileName: string;
  fileHash: string;
  status: string;
  summary: {
    totalRows: number;
    duplicateRows: number;
    errorRows: number;
    tradeRows: number;
    snapshotRows?: number;
    cashFlowRows?: number;
    dividendRows?: number;
    mtmRows?: number;
    skippedAssetClassRows?: number;
    skippedAssetClassCounts?: Record<string, number>;
    parserVersion?: string;
    fileHash: string;
  } | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
  rows?: ImportRowDto[];
}

export interface HlSecurityMappingDto {
  id: string;
  normalizedName: string;
  symbol: string;
  sourceDescription: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeleteImportDto {
  deleted: true;
  tradesRemoved: number;
}

export interface FlexConfigDto {
  id: string;
  accountId: string;
  tokenMasked: string;
  tradeQueryId: string;
  navQueryId: string | null;
  enabled: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FlexConfigInput {
  accountId: string;
  ibkrToken: string;
  tradeQueryId: string;
  navQueryId?: string | null;
  enabled: boolean;
}

export interface FlexTestInput {
  token: string;
  queryId: string;
}

export interface PurgeTradesDto {
  deleted: true;
  tradesRemoved: number;
}

export interface RecomputePositionsDto {
  positionsRecomputed: number;
}

export type PositionStatusFlag =
  | 'NO_STOP'
  | 'INVALID_STOP'
  | 'DATA_INCOMPLETE'
  | 'HISTORICAL_PNL_INCOMPLETE'
  | 'ORDERING_UNCERTAIN'
  | 'STOP_MOVED_ADVERSE'
  | 'HELD_PAST_PLAN'
  | 'OVERSIZED'
  | 'THEME_OVER_CAP'
  | 'MANUAL_OVERRIDE';

export interface RebuyHigherFlagDto {
  sellPrice: string;
  buyPrice: string;
  pctAbove: number;
  sellDate: string;
  buyDate: string;
}

export interface PositionDto {
  symbol: string;
  accountId: string;
  qty: string;
  side: 'LONG' | 'SHORT';
  avgCost: string;
  costBasis: string;
  currency: string;
  currentPrice: string;
  lastQuoteAt: string | null;
  priceSource: 'live' | 'cached' | 'last-fill';
  priceFetchedAt: string | null;
  priceStale: boolean;
  marketValue: string;
  pctNav: string;
  currentStop: string | null;
  stopCorrectionReason?: 'INVALID_FOR_POSITION_SIDE' | 'PREVIOUS_EPISODE' | null;
  plannedStop: string | null;
  distanceToStop: string | null;
  dollarRiskToStop: string | null;
  daysHeld: number;
  firstBuyDate: string;
  firstOpenedAt: string;
  realisedPnl: number | null;
  totalPnl: number | null;
  numberOfAdds: number;
  numberOfPartialExits: number;
  isRoundTripping: boolean;
  rebuyHigher?: RebuyHigherFlagDto | null;
  peakQty: number;
  plannedExitDate: string | null;
  tradeType: string | null;
  theme: string | null;
  isManualOverride: boolean;
  historyIncomplete?: boolean;
  historicalPnlIncomplete?: boolean;
  shortProvenance?: 'CONFIRMED' | 'AMBIGUOUS_PREHISTORY' | null;
  orderingUncertain?: boolean;
  statusFlags: PositionStatusFlag[];
  unrealizedPnlDollar: string | null;
  unrealizedPnlPct: string | null;
  rMultiple: string | null;
}

export interface BrokerVerifiedFlatResolutionDto {
  id: string;
  accountId: string;
  symbol: string;
  securityIdentity: string;
  observedAt: string;
  verifiedQuantity: '0';
  source: string;
  reference: string;
  latestExecutionId: string;
  latestExternalExecId: string;
  latestExecutionAt: string;
  executionSetFingerprint: string;
  recordedAt: string;
}

export interface UserConfirmedFlatResolutionDto {
  kind: 'USER_CONFIRMED';
  id: string;
  accountId: string;
  symbol: string;
  observedAt: string;
  verifiedQuantity: '0';
  source: 'AUTHENTICATED_USER';
  reference: string;
  confirmedByUserId: string;
  latestExecutionId: string;
  latestExternalExecId: string;
  latestExecutionAt: string;
  executionSetFingerprint: string;
  recordedAt: string;
}

export interface PositionTradeDto {
  id: string;
  tradeDate: string;
  tradeTime: string | null;
  side: string;
  quantity: string;
  price: string;
  netCash: string;
  commission: string;
  externalExecId: string;
}

export interface PositionReplayBarDto {
  date: string;
  adjustedOpen: string;
  adjustedHigh: string;
  adjustedLow: string;
  adjustedClose: string;
  adjustedVolume: string;
}

export interface PositionReplayExecutionDto {
  id: string;
  episodeId: number;
  date: string;
  side: 'BUY' | 'SELL';
  quantity: string;
  price: string;
  tradeTime: string | null;
  timeKnown: boolean;
}

export interface PositionReplayEpisodeDto {
  id: number;
  startedAt: string;
  endedAt: string | null;
  isOpen: boolean;
  flatDaysBefore: number | null;
}

export interface PositionReplayDto {
  symbol: string;
  scope: 'current' | 'full';
  bars: PositionReplayBarDto[];
  executions: PositionReplayExecutionDto[];
  episodes: PositionReplayEpisodeDto[];
  shortResolution: 'NONE' | 'CONFIRMED_SHORT' | 'AMBIGUOUS_HISTORY';
}

export interface PositionJournalEntryDto {
  id: string;
  entryType: string;
  body: string;
  thesis: string | null;
  tags: string[];
  createdAt: string;
}

export interface PositionDetailDto extends PositionDto {
  trades: PositionTradeDto[];
  journalEntries: PositionJournalEntryDto[];
}

export interface PositionLifecycleExitDto {
  date: string;
  qty: number;
  price: number;
  realisedPnl: number;
  daysHeldAtExit: number;
  type: 'PROFIT' | 'STOP' | 'UNKNOWN';
}

export interface PositionLifecycleEventDto {
  date: string;
  event: 'BUY' | 'SELL' | 'STOP_UPDATE' | 'PLAN';
  tradeId: string | null;
  qty: number | null;
  price: number | null;
  runningQty: number | null;
  realisedPnlToDate: number | null;
  pnlFromExit: number | null;
  pctClosed: number | null;
  oldStop: string | null;
  newStop: string | null;
  reason: string | null;
  planId: string | null;
  rulesResult: string | null;
  wasFollowed: boolean | null;
}

export interface PositionStopHistoryDto {
  id: string;
  changedAt: string | null;
  oldStop: string | null;
  newStop: string;
  changedBy: string | null;
  source: string;
  reason: string | null;
  isInitial: boolean;
}

export interface PositionLifecycleDto {
  symbol: string;
  status: 'OPEN' | 'CLOSED';
  firstBuyDate: string;
  daysHeld: number;
  peakQty: number;
  currentQty: number;
  totalSharesEverBought: number;
  totalSharesEverSold: number;
  originalPlannedQty: number;
  avgEntryPrice: number;
  avgCost: number;
  totalCostBasis: number;
  partialExits: PositionLifecycleExitDto[];
  realisedPnl: number;
  unrealisedPnl: number;
  totalPnl: number;
  realisedPnlPct: number;
  totalPnlPct: number;
  peakUnrealisedPnl: number;
  currentVsPeak: number;
  isRoundTripping: boolean;
  numberOfAdds: number;
  numberOfPartialExits: number;
  stopMigrations: number;
  planDrift: boolean;
  currentPrice: number;
  stopHistory: PositionStopHistoryDto[];
  timeline: PositionLifecycleEventDto[];
}

export interface RuleResultDto {
  key: string;
  status: 'PASS' | 'WARN' | 'BLOCK';
  value: number | null;
  threshold: number | null;
  message: string;
  resolution: {
    userConfiguredValue: number | boolean | null;
    systemValue: number | boolean | null;
    resolutionPolicy: 'MAXIMUM_CEILING' | 'MINIMUM_FLOOR' | 'REQUIRED_TRUE' | null;
    effectiveValue: number | boolean | null;
    source: 'USER_CONFIG' | 'SYSTEM_DEFAULT' | 'SYSTEM_BOUND' | 'RUNTIME';
  };
}

export interface GateDecisionDto {
  decision: 'APPROVED' | 'WARN' | 'BLOCKED';
  recommendedQty: number;
  positionPctNav: number;
  rewardRisk: number | null;
  todayBuyPctNav: number;
  themePctNav: number;
  portfolioHeat: number;
  portfolioHeatComplete?: boolean;
  riskPerTradePctNav: number;
  maxByStop?: number;
  maxBySize?: number;
  riskTierPct?: number;
  ruleResults: RuleResultDto[];
  reasons: string[];
}

export interface TradePlanDto {
  id: string;
  accountId: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  proposedEntry: string;
  plannedEntryMin: string | null;
  plannedEntryMax: string | null;
  proposedStop: string;
  proposedTarget: string | null;
  proposedQty: string | null;
  theme: string | null;
  tradeType: string;
  conviction: number;
  thesis: string;
  whyNow: string | null;
  invalidation: string | null;
  isFtdPilot: boolean;
  overrideReason: string | null;
  checklist: Record<string, boolean>;
  validUntil: string;
  status: TradePlanStatus;
  gateDecision: GateDecisionDto | null;
  createdAt: string;
  updatedAt: string;
}

export interface RuleConfigDto {
  id: string;
  ruleKey: string;
  value: number | boolean;
  enabled: boolean;
  severity: 'INFO' | 'WARN' | 'BLOCK';
  overridable: boolean;
  notes: string | null;
  updatedAt: string;
  createdAt: string;
}

export interface JournalEntryDto {
  id: string;
  userId: string;
  accountId: string | null;
  symbol: string | null;
  tradePlanId: string | null;
  tradeId: string | null;
  entryType: JournalEntryType;
  thesis: string | null;
  whyNow: string | null;
  invalidation: string | null;
  emotionalState: string | null;
  tags: string[];
  body: string;
  rulesFollowed: string[];
  rulesBroken: string[];
  outcomeR: string | null;
  whatWorkedWell: string | null;
  whatWentWrong: string | null;
  lessons: string | null;
  emotionalRegret: number | null;
  planAdherence?: string | null;
  dominantEmotion?: string | null;
  oneLineLesson?: string | null;
  whatHappened?: string | null;
  whatToChange?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewContextDto {
  trade: {
    id: string;
    symbol: string;
    side: string;
    realisedPnl: string;
    openedAt: string;
    closedAt: string;
    heldMinutes: number;
  };
  lifecycle: {
    id: string;
    accountId: string;
    symbol: string;
    status: 'OPEN' | 'CLOSED';
    firstEntryAt: string;
    lastExitAt: string | null;
    totalSharesBought: string;
    totalSharesSold: string;
    avgEntryPrice: string;
    avgExitPrice: string;
    totalInvested: string;
    totalExited: string;
    realisedPnl: string;
    totalCommissions: string;
    netRealisedPnl: string;
    fxImpact: string;
    fxRate: string;
    holdingPeriodDays: number | null;
    entries: Array<{
      tradeId: string;
      date: string;
      qty: string;
      price: string;
      grossAmount: string;
      runningTotalShares: string;
    }>;
    exits: Array<{
      tradeId: string;
      date: string;
      qty: string;
      price: string;
      grossAmount: string;
      pctOfPositionClosed: string;
      realisedPnl: string;
      runningTotalShares: string;
    }>;
  };
  reviewMode: ReviewMode;
  reviewMustBeSubmittedBy: string;
  rulesSummary: {
    passed: number;
    warned: number;
    broken: number;
    warnedRules: Array<{ code: string; message: string }>;
    brokenRules: Array<{ code: string; message: string }>;
    allRules: Array<{ code: string; status: 'passed' | 'warned' | 'broken' }>;
  };
  flagsFired: Array<{ code: string; description: string }>;
}

export interface JournalFilters {
  tradePlanId?: string;
  tradeId?: string;
  entryType?: JournalEntryType;
  symbol?: string;
  from?: string;
  to?: string;
}

export interface JournalLessonDto {
  date: string;
  symbol: string | null;
  lesson: string;
}

export interface BehaviouralFlagDto {
  id: string;
  kind: BehaviouralFlagKind;
  severity: Severity;
  subjectSymbol: string | null;
  subjectTradeId: string | null;
  payload: unknown;
  acknowledgedAt: string | null;
  status: 'ACTIVE' | 'ACKNOWLEDGED' | 'SUPERSEDED';
  createdAt: string;
}

export interface WeeklyReviewDto {
  id: string;
  weekStart: string;
  metrics: WeeklyReviewMetrics;
  flagsSummary: WeeklyReviewFlagsSummary;
  notes: string;
  generatedAt: string;
  updatedAt: string;
}

export interface NotificationDto {
  id: string;
  kind: NotificationKind;
  tradeId?: string | null;
  title: string;
  body: string;
  severity: Severity;
  readAt: string | null;
  deliverAt: string | null;
  createdAt: string;
}

export interface TradeReviewDto {
  id: string;
  tradeId: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  realisedPnl: string;
  closedAt: string;
  ageHours: number;
  reviewMode: ReviewMode | null;
  submittedAt: string | null;
  revision: number;
  updatedAt: string;
}

export interface PendingReviewsDto {
  items: TradeReviewDto[];
  count: number;
  oldestAgeDays: number | null;
}

export interface ReviewHistoryDto {
  id: string;
  revision: number;
  snapshot: unknown;
  replacedAt: string;
}

export interface FlagFilters {
  from?: string;
  kind?: BehaviouralFlagKind;
  unack?: boolean;
}

interface UserResponse {
  user: UserDto;
}

interface AuthResponse {
  user: UserDto;
  token: string;
}

interface AccountsResponse {
  accounts: AccountDto[];
}

interface AccountResponse {
  account: AccountDto;
}

interface SnapshotResponse {
  snapshot: SnapshotDto | null;
}

interface CombinedSnapshotResponse {
  snapshot: CombinedNavSnapshotDto | null;
}

interface SnapshotsResponse {
  snapshots: SnapshotDto[];
}

interface ImportsResponse {
  imports: ImportDto[];
}

interface ImportResponse {
  import: ImportDto;
  importId?: string;
}

interface HlSecurityMappingsResponse {
  mappings: HlSecurityMappingDto[];
}

interface HlSecurityMappingResponse {
  mapping: HlSecurityMappingDto;
}

interface FlexConfigResponse {
  config: FlexConfigDto | null;
}

export interface PositionsResponse {
  positions: PositionDto[];
  historyIncomplete: PositionDto[];
}

interface PositionResponse {
  position: PositionDetailDto;
}

interface PositionLifecycleResponse {
  lifecycle: PositionLifecycleDto;
}

export interface PositionIndicatorHistoryDto {
  status: 'READY' | 'INSUFFICIENT_HISTORY';
  requiredBars: number;
  barCount: number;
  priceData: OhlcPriceBar[];
}

interface PositionIndicatorHistoriesResponse {
  histories: Record<string, PositionIndicatorHistoryDto>;
}

interface AnalyticsSymbolLotsResponse {
  lots: AnalyticsSymbolLotDto[];
}

interface UpdatedPositionResponse {
  position: PositionDto;
}

interface TradeResponse {
  trade: PositionTradeDto;
}

interface SubmitReviewResponse {
  review: { id: string; tradeReviewId: string; reviewMode: ReviewMode; submittedAt: string };
}

export interface TradePlanRegimeDto {
  mode: 'DEFENSIVE' | 'REBUILD' | 'FULL';
  qqqPrice: number | null;
  qqqSma50: number | null;
  qqqAsOfDate: string | null;
  cleanExecutionCount: number;
  nextTierAt: number | null;
  riskTierPct: number;
}

export interface TradePlanGateResponse {
  plan: TradePlanDto | null;
  gateDecision: GateDecisionDto;
  regime: TradePlanRegimeDto;
}

interface TradePlansResponse {
  plans: TradePlanDto[];
}

interface RulesResponse {
  rules: RuleConfigDto[];
}

interface RuleResponse {
  rule: RuleConfigDto;
}

interface JournalEntriesResponse {
  entries: JournalEntryDto[];
}

interface JournalEntryResponse {
  entry: JournalEntryDto;
}

interface JournalLessonsResponse {
  lessons: JournalLessonDto[];
}

interface FlagsResponse {
  flags: BehaviouralFlagDto[];
}

interface FlagResponse {
  flag: BehaviouralFlagDto;
}

interface BulkAcknowledgeFlagsResponse {
  count: number;
}

interface WeeklyReviewsResponse {
  reviews: WeeklyReviewDto[];
}

interface WeeklyReviewResponse {
  review: WeeklyReviewDto;
}

interface NotificationsResponse {
  notifications: NotificationDto[];
}

interface TradeReviewsResponse {
  reviews: TradeReviewDto[];
}

type PendingReviewsResponse = PendingReviewsDto;

interface ReviewHistoryResponse {
  history: ReviewHistoryDto[];
}

interface NotificationResponse {
  notification: NotificationDto;
}

interface PortfolioMetricsResponse {
  metrics: PortfolioMetricsDto;
}

interface DrawdownHistoryResponse {
  history: DrawdownHistoryPointDto[];
}

interface PnlHistoryResponse {
  history: PnlHistoryPointDto[];
}

export interface AuthResult {
  user: UserDto;
  token: string;
}

export async function register(input: RegisterInput): Promise<AuthResult> {
  const { data } = await apiClient.post<AuthResponse>('/auth/register', input);
  return { user: data.user, token: data.token };
}

export async function login(input: LoginInput): Promise<AuthResult> {
  const { data } = await apiClient.post<AuthResponse>('/auth/login', input);
  return { user: data.user, token: data.token };
}

export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
}

export async function me(): Promise<UserDto> {
  const { data } = await apiClient.get<UserResponse>('/auth/me');
  return data.user;
}

export async function updatePreferences(input: UpdatePreferencesInput): Promise<UserDto> {
  const { data } = await apiClient.patch<UserResponse>('/auth/me/preferences', input);
  return data.user;
}

export async function listAccounts(): Promise<AccountDto[]> {
  const { data } = await apiClient.get<AccountsResponse>('/accounts');
  return data.accounts;
}

export async function listInvestmentHoldings(accountId: string): Promise<InvestmentHoldingsDto> {
  const { data } = await apiClient.get<InvestmentHoldingsDto>('/investment-holdings', {
    params: { accountId },
  });
  return data;
}

export async function getInvestmentAllocation(
  accountId: string,
): Promise<InvestmentAllocationResponse> {
  const { data } = await apiClient.get<InvestmentAllocationResponse>('/investment-allocation', {
    params: { accountId },
  });
  return data;
}

export async function listInvestmentTheses(
  accountId: string,
  includeClosed = false,
): Promise<InvestmentThesisDto[]> {
  const { data } = await apiClient.get<{ theses: InvestmentThesisDto[] }>('/investment-theses', {
    params: { accountId, includeClosed: String(includeClosed) },
  });
  return data.theses;
}

export async function createInvestmentThesis(
  input: CreateInvestmentThesisInput,
): Promise<InvestmentThesisDto> {
  const { data } = await apiClient.post<{ thesis: InvestmentThesisDto }>(
    '/investment-theses',
    input,
  );
  return data.thesis;
}

export async function updateInvestmentThesis(
  id: string,
  input: UpdateInvestmentThesisInput,
): Promise<InvestmentThesisDto> {
  const { data } = await apiClient.patch<{ thesis: InvestmentThesisDto }>(
    `/investment-theses/${id}`,
    input,
  );
  return data.thesis;
}

export async function rebindInvestmentThesis(
  id: string,
  input: RebindInvestmentThesisIdentityInput,
): Promise<InvestmentThesisDto> {
  const { data } = await apiClient.post<{ thesis: InvestmentThesisDto }>(
    `/investment-theses/${id}/rebind`,
    input,
  );
  return data.thesis;
}

export async function createInvestmentThesisSuccessor(
  id: string,
  input: CreateInvestmentThesisSuccessorInput,
): Promise<InvestmentThesisDto> {
  const { data } = await apiClient.post<{ thesis: InvestmentThesisDto }>(
    `/investment-theses/${id}/successor`,
    input,
  );
  return data.thesis;
}

export async function createAccount(input: CreateAccountInput): Promise<AccountDto> {
  const { data } = await apiClient.post<AccountResponse>('/accounts', input);
  return data.account;
}

export async function updateAccount(id: string, input: UpdateAccountInput): Promise<AccountDto> {
  const { data } = await apiClient.patch<AccountResponse>(`/accounts/${id}`, input);
  return data.account;
}

export async function deleteAccount(id: string): Promise<AccountDto> {
  const { data } = await apiClient.delete<AccountResponse>(`/accounts/${id}`);
  return data.account;
}

export async function latestSnapshot(accountId: string): Promise<SnapshotDto | null> {
  const { data } = await apiClient.get<SnapshotResponse>(`/accounts/${accountId}/snapshots/latest`);
  return data.snapshot;
}

export async function currentNavSnapshot(): Promise<CombinedNavSnapshotDto | null> {
  const { data } = await apiClient.get<CombinedSnapshotResponse>('/nav/current');
  return data.snapshot;
}

export async function snapshotHistory(input: {
  accountId: string;
  from: string;
  to: string;
}): Promise<SnapshotDto[]> {
  const { data } = await apiClient.get<SnapshotsResponse>(
    `/accounts/${input.accountId}/snapshots`,
    {
      params: { from: input.from, to: input.to },
    },
  );
  return data.snapshots;
}

export async function upsertSnapshot(
  accountId: string,
  input: AccountSnapshotInput,
): Promise<SnapshotDto> {
  const { data } = await apiClient.post<SnapshotResponse>(
    `/accounts/${accountId}/snapshots`,
    input,
  );
  if (!data.snapshot) {
    throw new Error('Snapshot write returned no snapshot.');
  }
  return data.snapshot;
}

export async function listImports(): Promise<ImportDto[]> {
  const { data } = await apiClient.get<ImportsResponse>('/imports');
  return data.imports;
}

export async function getImport(id: string, includeRows = false): Promise<ImportDto> {
  const { data } = await apiClient.get<ImportResponse>(`/imports/${id}`, {
    params: { includeRows },
  });
  return data.import;
}

export async function uploadImport(input: {
  accountId: string;
  broker: Broker;
  file: File;
}): Promise<ImportDto> {
  const formData = new FormData();
  formData.append('accountId', input.accountId);
  formData.append('broker', input.broker);
  formData.append('file', input.file);
  const { data } = await apiClient.post<ImportResponse>('/imports', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.import;
}

export async function commitImport(id: string): Promise<ImportDto> {
  const { data } = await apiClient.post<ImportResponse>(`/imports/${id}/commit`);
  return data.import;
}

export async function resetImport(id: string): Promise<ImportDto> {
  const { data } = await apiClient.post<ImportResponse>(`/imports/${id}/reset`);
  return data.import;
}

export async function deleteImport(id: string): Promise<DeleteImportDto> {
  const { data } = await apiClient.delete<DeleteImportDto>(`/imports/${id}`);
  return data;
}

export async function discardImport(id: string): Promise<DeleteImportDto> {
  return deleteImport(id);
}

export async function purgeAllTrades(accountId: string): Promise<PurgeTradesDto> {
  const { data } = await apiClient.delete<PurgeTradesDto>(`/accounts/${accountId}/trades`, {
    params: { confirm: 'PURGE_ALL_TRADES' },
  });
  return data;
}

export async function getFlexConfig(accountId: string): Promise<FlexConfigDto | null> {
  const { data } = await apiClient.get<FlexConfigResponse>('/flex/config', {
    params: { accountId },
  });
  return data.config;
}

export async function saveFlexConfig(input: FlexConfigInput): Promise<FlexConfigDto> {
  const { data } = await apiClient.post<FlexConfigResponse>('/flex/config', input);
  if (!data.config) {
    throw new Error('Flex config write returned no config.');
  }
  return data.config;
}

export async function disableFlexConfig(accountId: string): Promise<{ disabled: true }> {
  const { data } = await apiClient.delete<{ disabled: true }>('/flex/config', {
    params: { accountId },
  });
  return data;
}

export async function testFlexConnection(input: FlexTestInput): Promise<{ ok: true }> {
  const { data } = await apiClient.post<{ ok: true }>('/flex/test', input);
  return data;
}

export async function syncFlexNow(accountId: string): Promise<{ queued: true }> {
  const { data } = await apiClient.post<{ queued: true }>('/flex/sync-now', null, {
    params: { accountId },
  });
  return data;
}

export async function recomputePositions(accountId: string): Promise<RecomputePositionsDto> {
  const { data } = await apiClient.post<RecomputePositionsDto>(
    `/accounts/${accountId}/recompute-positions`,
  );
  return data;
}

export async function portfolioSummary(accountId: string): Promise<PortfolioSummaryDto> {
  const { data } = await apiClient.get<PortfolioSummaryDto>(
    `/accounts/${accountId}/portfolio-summary`,
  );
  return data;
}

export async function listPositions(input: {
  accountId: string;
  includeFlags?: boolean;
}): Promise<PositionsResponse> {
  const { data } = await apiClient.get<PositionsResponse>('/positions', {
    params: { accountId: input.accountId, includeFlags: input.includeFlags ?? true },
  });
  return data;
}

export async function listTradingNextActions(
  accountId: string,
): Promise<TradingNextActionsResponse['tradingNextActions']> {
  const { data } = await apiClient.get<TradingNextActionsResponse>('/trading-next-actions', {
    params: { accountId },
  });
  return data.tradingNextActions;
}

export async function getTradingPortfolioHeat(
  accountId: string,
): Promise<TradingPortfolioHeatResponse> {
  const { data } = await apiClient.get<TradingPortfolioHeatResponse>('/trading-portfolio-heat', {
    params: { accountId },
  });
  return data;
}

export async function getPosition(input: {
  accountId: string;
  symbol: string;
}): Promise<PositionDetailDto> {
  const { data } = await apiClient.get<PositionResponse>(`/positions/${input.symbol}`, {
    params: { accountId: input.accountId },
  });
  return data.position;
}

export async function getPositionLifecycle(input: {
  accountId: string;
  symbol: string;
}): Promise<PositionLifecycleDto> {
  const { data } = await apiClient.get<PositionLifecycleResponse>(
    `/positions/${input.symbol}/lifecycle`,
    {
      params: { accountId: input.accountId },
    },
  );
  return data.lifecycle;
}

export async function getPositionReplay(input: {
  accountId: string;
  symbol: string;
  scope?: 'current' | 'full';
}): Promise<PositionReplayDto> {
  const { data } = await apiClient.get<PositionReplayDto>(
    `/positions/${encodeURIComponent(input.symbol)}/replay`,
    {
      params: {
        accountId: input.accountId,
        ...(input.scope ? { scope: input.scope } : {}),
      },
    },
  );
  return data;
}

export async function getPositionPriceHistory(input: {
  accountId: string;
  symbol: string;
}): Promise<PositionIndicatorHistoryDto> {
  const { data } = await apiClient.get<PositionIndicatorHistoryDto>(
    `/positions/${encodeURIComponent(input.symbol)}/price-history`,
    {
      params: { accountId: input.accountId },
    },
  );
  return data;
}

export async function getPositionPriceHistories(input: {
  accountId: string;
  symbols: readonly string[];
}): Promise<Record<string, PositionIndicatorHistoryDto>> {
  const { data } = await apiClient.get<PositionIndicatorHistoriesResponse>(
    '/positions/price-history',
    {
      params: { accountId: input.accountId, symbols: input.symbols.join(',') },
    },
  );
  return data.histories;
}

export async function updatePositionStop(input: {
  accountId: string;
  symbol: string;
  body: UpdatePositionStopInput;
}): Promise<PositionDto> {
  const { data } = await apiClient.patch<UpdatedPositionResponse>(
    `/positions/${input.symbol}/discipline-stop`,
    input.body,
    {
      params: { accountId: input.accountId },
    },
  );
  return data.position;
}

export async function updatePositionOverride(input: {
  accountId: string;
  symbol: string;
  body: UpdatePositionOverrideInput;
}): Promise<PositionDto> {
  const { data } = await apiClient.patch<UpdatedPositionResponse>(
    `/positions/${input.symbol}`,
    input.body,
    {
      params: { accountId: input.accountId },
    },
  );
  return data.position;
}

export async function recordBrokerVerifiedFlat(input: {
  accountId: string;
  symbol: string;
  body: BrokerVerifiedFlatInput;
}): Promise<BrokerVerifiedFlatResolutionDto> {
  const { data } = await apiClient.post<{ resolution: BrokerVerifiedFlatResolutionDto }>(
    `/positions/${encodeURIComponent(input.symbol)}/broker-verified-flat`,
    input.body,
    { params: { accountId: input.accountId } },
  );
  return data.resolution;
}

export async function recordUserConfirmedFlat(input: {
  accountId: string;
  symbol: string;
  body: UserConfirmedFlatInput;
}): Promise<UserConfirmedFlatResolutionDto> {
  const { data } = await apiClient.post<{ resolution: UserConfirmedFlatResolutionDto }>(
    `/positions/${encodeURIComponent(input.symbol)}/user-confirmed-flat`,
    input.body,
    { params: { accountId: input.accountId } },
  );
  return data.resolution;
}

export async function deletePositionOverride(input: {
  accountId: string;
  symbol: string;
}): Promise<PositionDto> {
  const { data } = await apiClient.delete<UpdatedPositionResponse>(
    `/positions/${input.symbol}/override`,
    {
      params: { accountId: input.accountId },
    },
  );
  return data.position;
}

export async function updateTrade(input: {
  tradeId: string;
  body: UpdateTradeInput;
}): Promise<PositionTradeDto> {
  const { data } = await apiClient.patch<TradeResponse>(`/trades/${input.tradeId}`, input.body);
  return data.trade;
}

export async function deleteTrade(tradeId: string): Promise<PositionTradeDto> {
  const { data } = await apiClient.delete<TradeResponse>(`/trades/${tradeId}`, {
    params: { confirm: 'DELETE_TRADE' },
  });
  return data.trade;
}

export async function getReviewContext(tradeId: string): Promise<ReviewContextDto> {
  const { data } = await apiClient.get<ReviewContextDto>(`/trades/${tradeId}/review-context`);
  return data;
}

export async function submitTradeReview(input: {
  tradeId: string;
  body: SubmitTradeReviewInput;
}): Promise<SubmitReviewResponse['review']> {
  const { data } = await apiClient.post<SubmitReviewResponse>(
    `/trades/${input.tradeId}/review`,
    input.body,
  );
  return data.review;
}

export async function patchTradeReview(input: {
  tradeId: string;
  body: SubmitTradeReviewInput;
}): Promise<SubmitReviewResponse['review']> {
  const { data } = await apiClient.patch<SubmitReviewResponse>(
    `/trades/${input.tradeId}/review`,
    input.body,
  );
  return data.review;
}

export async function getPendingReviews(): Promise<PendingReviewsDto> {
  const { data } = await apiClient.get<PendingReviewsResponse>('/reviews/pending');
  return data;
}

export async function listTradeReviews(input: {
  status: 'pending' | 'submitted' | 'all';
  limit?: number;
  cursor?: string;
  symbol?: string;
}): Promise<TradeReviewDto[]> {
  const { data } = await apiClient.get<TradeReviewsResponse>('/reviews', { params: input });
  return data.reviews;
}

export async function getTradeReviewHistory(tradeId: string): Promise<ReviewHistoryDto[]> {
  const { data } = await apiClient.get<ReviewHistoryResponse>(`/reviews/trades/${tradeId}/history`);
  return data.history;
}

export async function createTradePlan(input: TradePlanInput): Promise<TradePlanGateResponse> {
  const { data } = await apiClient.post<TradePlanGateResponse>('/trade-plans', input);
  return data;
}

export async function previewTradePlan(
  input: TradePlanPreviewInput,
): Promise<TradePlanGateResponse> {
  const { data } = await apiClient.post<TradePlanGateResponse>('/trade-plans/preview', input);
  return data;
}

export async function listTradePlans(status?: TradePlanStatus): Promise<TradePlanDto[]> {
  const { data } = await apiClient.get<TradePlansResponse>('/trade-plans', {
    params: status ? { status } : {},
  });
  return data.plans;
}

export async function recheckTradePlan(id: string): Promise<TradePlanGateResponse> {
  const { data } = await apiClient.post<TradePlanGateResponse>(`/trade-plans/${id}/recheck`);
  return data;
}

export async function executeTradePlan(id: string): Promise<TradePlanDto> {
  const { data } = await apiClient.post<{ plan: TradePlanDto }>(`/trade-plans/${id}/execute`);
  return data.plan;
}

export async function listRules(): Promise<RuleConfigDto[]> {
  const { data } = await apiClient.get<RulesResponse>('/rules');
  return data.rules;
}

export async function updateRule(
  key: string,
  input: RuleConfigUpdateInput,
): Promise<RuleConfigDto> {
  const { data } = await apiClient.patch<RuleResponse>(`/rules/${key}`, input);
  return data.rule;
}

export async function listHlSecurityMappings(): Promise<HlSecurityMappingDto[]> {
  const { data } = await apiClient.get<HlSecurityMappingsResponse>('/hl-security-mappings');
  return data.mappings;
}

export async function upsertHlSecurityMapping(
  input: UpsertHlSecurityMappingInput,
): Promise<HlSecurityMappingDto> {
  const { data } = await apiClient.post<HlSecurityMappingResponse>('/hl-security-mappings', input);
  return data.mapping;
}

export async function updateHlSecurityMapping(input: {
  id: string;
  body: UpsertHlSecurityMappingInput;
}): Promise<HlSecurityMappingDto> {
  const { data } = await apiClient.patch<HlSecurityMappingResponse>(
    `/hl-security-mappings/${input.id}`,
    input.body,
  );
  return data.mapping;
}

export async function deleteHlSecurityMapping(id: string): Promise<{
  deleted: true;
  mapping: HlSecurityMappingDto;
}> {
  const { data } = await apiClient.delete<{ deleted: true; mapping: HlSecurityMappingDto }>(
    `/hl-security-mappings/${id}`,
  );
  return data;
}

export async function previewOAuthAuthorization(
  params: OAuthAuthorizeParams,
): Promise<OAuthAuthorizePreviewDto> {
  const { data } = await apiClient.post<OAuthAuthorizePreviewDto>(
    '/oauth/authorize/preview',
    params,
  );
  return data;
}

export async function approveOAuthAuthorization(
  params: OAuthAuthorizeParams,
): Promise<OAuthRedirectDto> {
  const { data } = await apiClient.post<OAuthRedirectDto>('/oauth/authorize/approve', params);
  return data;
}

export async function denyOAuthAuthorization(
  params: OAuthAuthorizeParams,
): Promise<OAuthRedirectDto> {
  const { data } = await apiClient.post<OAuthRedirectDto>('/oauth/authorize/deny', params);
  return data;
}

export async function listOAuthGrants(): Promise<OAuthGrantDto[]> {
  const { data } = await apiClient.get<{ grants: OAuthGrantDto[] }>('/oauth/grants');
  return data.grants;
}

export async function revokeOAuthGrant(id: string): Promise<void> {
  await apiClient.delete(`/oauth/grants/${id}`);
}

export async function listJournalEntries(filters: JournalFilters): Promise<JournalEntryDto[]> {
  const { data } = await apiClient.get<JournalEntriesResponse>('/journal', { params: filters });
  return data.entries;
}

export async function getJournalEntry(id: string): Promise<JournalEntryDto[]> {
  const { data } = await apiClient.get<JournalEntriesResponse>(`/journal/${id}`);
  return data.entries;
}

export async function createJournalEntry(input: CreateJournalEntryInput): Promise<JournalEntryDto> {
  const { data } = await apiClient.post<JournalEntryResponse>('/journal', input);
  return data.entry;
}

export async function updateJournalEntry(input: {
  id: string;
  data: UpdateJournalEntryInput;
}): Promise<JournalEntryDto> {
  const { data } = await apiClient.patch<JournalEntryResponse>(`/journal/${input.id}`, input.data);
  return data.entry;
}

export async function listJournalLessons(): Promise<JournalLessonDto[]> {
  const { data } = await apiClient.get<JournalLessonsResponse>('/journal/lessons');
  return data.lessons;
}

export async function createJournalReminder(symbol: string): Promise<void> {
  await apiClient.post('/journal/reminders', { symbol });
}

export async function listFlags(filters: FlagFilters = {}): Promise<BehaviouralFlagDto[]> {
  const { data } = await apiClient.get<FlagsResponse>('/flags', { params: filters });
  return data.flags;
}

export async function acknowledgeFlag(id: string): Promise<BehaviouralFlagDto> {
  const { data } = await apiClient.post<FlagResponse>(`/flags/${id}/ack`);
  return data.flag;
}

export async function acknowledgeAllFlags(): Promise<BulkAcknowledgeFlagsResponse> {
  const { data } = await apiClient.post<BulkAcknowledgeFlagsResponse>('/flags/ack-all');
  return data;
}

export async function listWeeklyReviews(): Promise<WeeklyReviewDto[]> {
  const { data } = await apiClient.get<WeeklyReviewsResponse>('/reviews/weekly');
  return data.reviews;
}

export async function getWeeklyReview(weekStart: string): Promise<WeeklyReviewDto> {
  const { data } = await apiClient.get<WeeklyReviewResponse>(`/reviews/weekly/${weekStart}`);
  return data.review;
}

export async function regenerateWeeklyReview(weekStart: string): Promise<WeeklyReviewDto> {
  const { data } = await apiClient.post<WeeklyReviewResponse>(
    `/reviews/weekly/${weekStart}/regenerate`,
  );
  return data.review;
}

export async function updateWeeklyReviewNotes(input: {
  weekStart: string;
  body: WeeklyReviewNotesInput;
}): Promise<WeeklyReviewDto> {
  const { data } = await apiClient.patch<WeeklyReviewResponse>(
    `/reviews/weekly/${input.weekStart}/notes`,
    input.body,
  );
  return data.review;
}

export async function portfolioMetrics(accountId: string): Promise<PortfolioMetricsDto> {
  const { data } = await apiClient.get<PortfolioMetricsResponse>('/analytics/portfolio', {
    params: { accountId },
  });
  return data.metrics;
}

export async function drawdownHistory(input: {
  accountId: string;
  from: string;
  to: string;
}): Promise<DrawdownHistoryPointDto[]> {
  const { data } = await apiClient.get<DrawdownHistoryResponse>('/analytics/drawdown', {
    params: { ...input, granularity: 'day' },
  });
  return data.history;
}

export async function pnlHistory(input: {
  accountId: string;
  from: string;
  to: string;
  groupBy: 'day' | 'week' | 'month';
}): Promise<PnlHistoryPointDto[]> {
  const { data } = await apiClient.get<PnlHistoryResponse>('/analytics/pnl', {
    params: input,
  });
  return data.history;
}

export async function analyticsOverview(input: {
  accountId: string;
  period: AnalyticsPeriod;
}): Promise<AnalyticsOverviewDto> {
  const { data } = await apiClient.get<AnalyticsOverviewDto>('/analytics/overview', {
    params: input,
  });
  return data;
}

export async function analyticsCalendar(input: {
  accountId: string;
  year: number;
}): Promise<AnalyticsCalendarDayDto[]> {
  const { data } = await apiClient.get<AnalyticsCalendarDayDto[]>('/analytics/calendar', {
    params: input,
  });
  return data;
}

export async function analyticsBySymbol(input: {
  accountId: string;
  period: AnalyticsPeriod;
  page?: number;
  limit?: number;
}): Promise<AnalyticsSymbolDto[]> {
  const { data } = await apiClient.get<AnalyticsSymbolDto[]>('/analytics/by-symbol', {
    params: input,
  });
  return data;
}

export async function analyticsSymbolLots(input: {
  accountId: string;
  symbol: string;
}): Promise<AnalyticsSymbolLotDto[]> {
  const { data } = await apiClient.get<AnalyticsSymbolLotsResponse>(
    `/analytics/symbols/${input.symbol}/lots`,
    {
      params: { accountId: input.accountId },
    },
  );
  return data.lots;
}

export async function analyticsClosedLots(input: {
  accountId: string;
  from: string;
  to: string;
}): Promise<AnalyticsClosedLotsDto> {
  const { data } = await apiClient.get<AnalyticsClosedLotsDto>('/analytics/closed-lots', {
    params: input,
  });
  return data;
}

export async function analyticsFlagImpact(accountId: string): Promise<AnalyticsFlagImpactDto> {
  const { data } = await apiClient.get<AnalyticsFlagImpactDto>('/analytics/flag-impact', {
    params: { accountId },
  });
  return data;
}

export async function analyticsAnnualReturns(
  accountId: string,
): Promise<AccountAnnualReturnsResponse> {
  const { data } = await apiClient.get<AccountAnnualReturnsResponse>('/analytics/annual-returns', {
    params: { accountId },
  });
  return data;
}

export async function listNotifications(filters: {
  unread?: boolean;
  limit?: number;
}): Promise<NotificationDto[]> {
  const { data } = await apiClient.get<NotificationsResponse>('/notifications', {
    params: filters,
  });
  return data.notifications;
}

export async function unreadNotificationCount(): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/notifications/unread-count');
  return data.count;
}

export async function markNotificationRead(id: string): Promise<NotificationDto> {
  const { data } = await apiClient.post<NotificationResponse>(`/notifications/${id}/read`);
  return data.notification;
}

export async function markAllNotificationsRead(): Promise<{ count: number }> {
  const { data } = await apiClient.post<{ count: number }>('/notifications/read-all');
  return data;
}

export async function dismissReviewPendingNotification(
  tradeId: string,
): Promise<{ count: number }> {
  const { data } = await apiClient.delete<{ count: number }>(
    `/notifications/review-pending/${tradeId}`,
  );
  return data;
}
